import { useEffect, useMemo, useRef, useState } from "react";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import { type ClienteNexo, type NodoRed, type RedNexo, vizRed } from "../api.ts";
import { armarCsv, descargarCsv } from "../csv.ts";
import { C, flagAnillo, nodoColor, schemaEs, st } from "../theme.ts";
import { Cargando, ErrorBox } from "../ui/componentes.tsx";
import { rutaAHash } from "../rutas.ts";

/** Nodo con posición del layout (d3-force muta x/y sobre el objeto). */
type SimNodo = NodoRed & SimulationNodeDatum & { grado: number };
type SimLink = SimulationLinkDatum<SimNodo> & { schema: string; rol: string | null };
/**
 * Arista para RENDER: solo ids planos. Las líneas buscan la posición vigente
 * en el estado de nodos por id — si guardaran la referencia al objeto del
 * layout, el drag (que reemplaza el objeto en el estado) movería el nodo
 * dejando las líneas clavadas en la posición vieja.
 */
interface AristaPos {
  src: string;
  dst: string;
  schema: string;
  rol: string | null;
}

const ANCHO_VIRTUAL = 1200;
const ALTO_VIRTUAL = 760;
/** Con más nodos que esto, solo se etiquetan los de distancia ≤ 1 (el resto por hover). */
const MAX_NODOS_ETIQUETADOS = 80;
const TICKS_LAYOUT = 300;
const CLICK_TOLERANCIA_PX = 5;

function radioNodo(n: SimNodo, esCentro: boolean): number {
  const r = 5 + Math.min(9, Math.sqrt(n.grado) * 1.9);
  return esCentro ? Math.max(r, 11) : r;
}

/**
 * Corre el layout force-directed SÍNCRONO (sin animación): con ≤400 nodos los
 * 300 ticks toman decenas de ms y el grafo aparece ya asentado. El drag
 * posterior mueve nodos a mano sin re-simular.
 */
function calcularLayout(red: RedNexo): { nodos: SimNodo[]; aristas: AristaPos[] } {
  const grado = new Map<string, number>();
  for (const a of red.aristas) {
    grado.set(a.src, (grado.get(a.src) ?? 0) + 1);
    grado.set(a.dst, (grado.get(a.dst) ?? 0) + 1);
  }
  const nodos: SimNodo[] = red.nodos.map((n) => ({ ...n, grado: grado.get(n.id) ?? 0 }));
  const links: SimLink[] = red.aristas.map((a) => ({ source: a.src, target: a.dst, schema: a.schema, rol: a.rol }));

  const sim = forceSimulation(nodos)
    .force("link", forceLink<SimNodo, SimLink>(links).id((d) => d.id).distance(85).strength(0.5))
    .force("charge", forceManyBody().strength(-320))
    .force("center", forceCenter(ANCHO_VIRTUAL / 2, ALTO_VIRTUAL / 2))
    .force("collide", forceCollide<SimNodo>().radius((d) => radioNodo(d, false) + 10))
    .stop();
  for (let i = 0; i < TICKS_LAYOUT; i++) sim.tick();

  // Zoom-to-fit: escala uniforme del layout al lienzo virtual con margen,
  // para que la red aproveche el espacio sin importar cuánto se expandió.
  const xs = nodos.map((n) => n.x ?? 0);
  const ys = nodos.map((n) => n.y ?? 0);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const margen = 70;
  const escala = Math.min(
    (ANCHO_VIRTUAL - 2 * margen) / Math.max(1, maxX - minX),
    (ALTO_VIRTUAL - 2 * margen) / Math.max(1, maxY - minY),
  );
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  for (const n of nodos) {
    n.x = ANCHO_VIRTUAL / 2 + ((n.x ?? 0) - cx) * escala;
    n.y = ALTO_VIRTUAL / 2 + ((n.y ?? 0) - cy) * escala;
  }
  return {
    nodos,
    aristas: links.map((l) => ({
      src: (l.source as SimNodo).id,
      dst: (l.target as SimNodo).id,
      schema: l.schema,
      rol: l.rol,
    })),
  };
}

interface Transform {
  k: number;
  tx: number;
  ty: number;
}

/**
 * Red — el vecindario de una entidad a 1–3 saltos: nodos coloreados por tipo
 * (paleta categórica validada + etiqueta directa + leyenda), halo oro para el
 * centro, anillo rojo para sanción vigente. Interacción: zoom con rueda, pan
 * arrastrando el fondo, arrastrar nodos, clic → ficha.
 */
export function Red({ cliente, id, saltos }: { cliente: ClienteNexo; id: string; saltos: number }) {
  const [red, setRed] = useState<RedNexo | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [nodos, setNodos] = useState<SimNodo[]>([]);
  const [aristas, setAristas] = useState<AristaPos[]>([]);
  const [tf, setTf] = useState<Transform>({ k: 1, tx: 0, ty: 0 });
  const [hover, setHover] = useState<{ nodo: SimNodo; x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Drag en curso: de nodo (por id) o de fondo (pan). Refs — no re-render por movimiento del pointer.
  const drag = useRef<{ tipo: "nodo"; id: string; movido: number } | { tipo: "pan"; x0: number; y0: number; tx0: number; ty0: number } | null>(null);

  useEffect(() => {
    let vivo = true;
    setRed(undefined);
    setError(null);
    vizRed(cliente, id, saltos)
      .then((r) => {
        if (!vivo) return;
        setRed(r);
        if (r) {
          const { nodos: ns, aristas: as } = calcularLayout(r);
          setNodos(ns);
          setAristas(as);
          setTf({ k: 1, tx: 0, ty: 0 });
        }
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, id, saltos]);

  const centro = useMemo(() => nodos.find((n) => n.id === id), [nodos, id]);
  const etiquetarTodos = nodos.length <= MAX_NODOS_ETIQUETADOS;
  const schemasPresentes = useMemo(() => [...new Set(nodos.map((n) => n.schema))], [nodos]);
  /** Posición vigente por id — las líneas siguen al nodo aunque el drag lo reemplace. */
  const porId = useMemo(() => new Map(nodos.map((n) => [n.id, n])), [nodos]);

  /** Coordenadas de pantalla → coordenadas del lienzo virtual (deshace el transform). */
  function aLienzo(clientX: number, clientY: number): { x: number; y: number } {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const sx = ((clientX - rect.left) / rect.width) * ANCHO_VIRTUAL;
    const sy = ((clientY - rect.top) / rect.height) * ALTO_VIRTUAL;
    return { x: (sx - tf.tx) / tf.k, y: (sy - tf.ty) / tf.k };
  }

  function onWheel(e: React.WheelEvent<SVGSVGElement>) {
    const factor = Math.exp(-e.deltaY * 0.0015);
    setTf((t) => {
      const k = Math.min(6, Math.max(0.25, t.k * factor));
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return { ...t, k };
      const sx = ((e.clientX - rect.left) / rect.width) * ANCHO_VIRTUAL;
      const sy = ((e.clientY - rect.top) / rect.height) * ALTO_VIRTUAL;
      // Zoom hacia el cursor: el punto bajo el cursor no se mueve.
      return { k, tx: sx - ((sx - t.tx) / t.k) * k, ty: sy - ((sy - t.ty) / t.k) * k };
    });
  }

  function onPointerDownFondo(e: React.PointerEvent<SVGSVGElement>) {
    if (drag.current) return;
    drag.current = { tipo: "pan", x0: e.clientX, y0: e.clientY, tx0: tf.tx, ty0: tf.ty };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (d.tipo === "pan") {
      const dx = ((e.clientX - d.x0) / rect.width) * ANCHO_VIRTUAL;
      const dy = ((e.clientY - d.y0) / rect.height) * ALTO_VIRTUAL;
      setTf((t) => ({ ...t, tx: d.tx0 + dx, ty: d.ty0 + dy }));
    } else {
      d.movido += Math.abs(e.movementX) + Math.abs(e.movementY);
      const p = aLienzo(e.clientX, e.clientY);
      setNodos((ns) => ns.map((n) => (n.id === d.id ? { ...n, x: p.x, y: p.y } : n)));
      setHover(null);
    }
  }

  function onPointerUp() {
    drag.current = null;
  }

  function onPointerDownNodo(e: React.PointerEvent<SVGGElement>, n: SimNodo) {
    e.stopPropagation();
    drag.current = { tipo: "nodo", id: n.id, movido: 0 };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }

  function onPointerUpNodo(e: React.PointerEvent<SVGGElement>, n: SimNodo) {
    const d = drag.current;
    drag.current = null;
    // Clic limpio (sin arrastre) → ficha de la entidad.
    if (d && d.tipo === "nodo" && d.id === n.id && d.movido <= CLICK_TOLERANCIA_PX) {
      e.stopPropagation();
      window.location.hash = rutaAHash({ vista: "entidad", id: n.id });
    }
  }

  function exportarAristasCsv() {
    if (!red) return;
    const porId = new Map(red.nodos.map((n) => [n.id, n]));
    const csv = armarCsv(
      ["origen", "tipo_origen", "relacion", "destino", "tipo_destino", "rol", "porcentaje", "origen_id", "destino_id"],
      red.aristas.map((a) => {
        const s = porId.get(a.src);
        const d = porId.get(a.dst);
        return [
          s?.caption, s ? schemaEs(s.schema) : "", a.schema === "LinkedTo" ? "Linked To" : a.schema,
          d?.caption, d ? schemaEs(d.schema) : "", a.rol, a.porcentaje, a.src, a.dst,
        ];
      }),
    );
    const hoy = new Date().toISOString().slice(0, 10);
    descargarCsv(`argus-nexo-red-${(centro?.caption ?? id).slice(0, 30).replace(/[^\w]+/g, "_")}-${hoy}.csv`, csv);
  }

  if (error) return <ErrorBox mensaje={error} />;
  if (red === undefined) return <Cargando texto="Trazando la red…" />;
  if (red === null) return <ErrorBox mensaje="No existe una entidad con ese identificador." />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", margin: "4px 0 10px" }}>
        <a
          href={rutaAHash({ vista: "entidad", id })}
          style={{ fontSize: 13, color: C.tiel, textDecoration: "none", fontWeight: 600 }}
        >
          ← Ficha
        </a>
        <h1 style={{ ...st.display, fontSize: 22, margin: 0 }}>
          Red de {centro?.caption ?? "entidad"}
        </h1>
        <span style={{ fontSize: 13, color: C.muted }}>
          {String(nodos.length)} nodos · {String(aristas.length)} relaciones
        </span>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: C.ink }} htmlFor="saltos">Saltos</label>
        <select
          id="saltos"
          style={{ ...st.input, width: "auto", padding: "6px 10px" }}
          value={String(saltos)}
          onChange={(e) => {
            window.location.hash = rutaAHash({ vista: "red", id, saltos: Number(e.target.value) });
          }}
        >
          <option value="1">1 — vecinos directos</option>
          <option value="2">2 — el entorno</option>
          <option value="3">3 — la constelación</option>
        </select>
        <button style={st.btn} onClick={exportarAristasCsv}>Exportar relaciones CSV</button>
        <button
          style={st.btn}
          onClick={() => {
            setTf({ k: 1, tx: 0, ty: 0 });
          }}
        >
          Reencuadrar
        </button>
        {red.truncado ? (
          <span style={{ fontSize: 12.5, color: C.amber, fontWeight: 700 }}>
            Red truncada a 400 nodos (los más cercanos) — baja los saltos para ver el detalle.
          </span>
        ) : null}
      </div>

      <div style={{ position: "relative", background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, overflow: "hidden" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${String(ANCHO_VIRTUAL)} ${String(ALTO_VIRTUAL)}`}
          style={{ display: "block", width: "100%", height: "min(72vh, 760px)", cursor: "grab", touchAction: "none" }}
          onWheel={onWheel}
          onPointerDown={onPointerDownFondo}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => {
            setHover(null);
            drag.current = null;
          }}
          role="img"
          aria-label={`Red de relaciones de ${centro?.caption ?? "la entidad"}: ${String(nodos.length)} nodos y ${String(aristas.length)} relaciones. La tabla de relaciones equivalente está en la ficha.`}
        >
          <g transform={`translate(${String(tf.tx)},${String(tf.ty)}) scale(${String(tf.k)})`}>
            {aristas.map((a, i) => {
              const s = porId.get(a.src);
              const d = porId.get(a.dst);
              if (!s || !d) return null;
              return (
                <line
                  key={i}
                  x1={s.x ?? 0}
                  y1={s.y ?? 0}
                  x2={d.x ?? 0}
                  y2={d.y ?? 0}
                  stroke="#C9BFA9"
                  strokeWidth={1.1 / tf.k}
                />
              );
            })}
            {nodos.map((n) => {
              const esCentro = n.id === id;
              const r = radioNodo(n, esCentro);
              const etiquetar = etiquetarTodos || n.dist <= 1;
              return (
                <g
                  key={n.id}
                  transform={`translate(${String(n.x ?? 0)},${String(n.y ?? 0)})`}
                  style={{ cursor: "pointer" }}
                  onPointerDown={(e) => { onPointerDownNodo(e, n); }}
                  onPointerUp={(e) => { onPointerUpNodo(e, n); }}
                  onPointerEnter={(e) => {
                    if (!drag.current) setHover({ nodo: n, x: e.clientX, y: e.clientY });
                  }}
                  onPointerLeave={() => {
                    setHover((h) => (h?.nodo.id === n.id ? null : h));
                  }}
                >
                  {esCentro ? (
                    <circle r={r + 5} fill="none" stroke={C.oro} strokeWidth={2.5} />
                  ) : null}
                  {n.sancionado ? (
                    <circle r={r + 2.2} fill="none" stroke={C.red} strokeWidth={1.1} opacity={0.85} />
                  ) : null}
                  {/* Eje propio (0078): anillo PUNTEADO por severidad fiscal — nunca se
                      confunde con el anillo sólido de sanción (dos ejes, dos vestimentas). */}
                  {flagAnillo(n.flag_severidad) ? (
                    <circle
                      r={r + (n.sancionado ? 4.4 : 2.2)}
                      fill="none"
                      stroke={flagAnillo(n.flag_severidad) ?? C.amber}
                      strokeWidth={1.1}
                      strokeDasharray="2.4 2"
                      opacity={0.9}
                    />
                  ) : null}
                  <circle r={r} fill={nodoColor(n.schema)} stroke="#fff" strokeWidth={1.6} />
                  {etiquetar ? (
                    <text
                      y={r + 11}
                      textAnchor="middle"
                      style={{
                        fontSize: Math.max(9, 10.5 / Math.sqrt(tf.k)),
                        fill: C.ink,
                        paintOrder: "stroke",
                        stroke: "#fff",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                        pointerEvents: "none",
                        fontWeight: esCentro ? 700 : 400,
                      }}
                    >
                      {(n.caption ?? "").length > 34 ? `${(n.caption ?? "").slice(0, 32)}…` : n.caption ?? ""}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>

        {hover ? (
          <div
            style={{
              position: "fixed",
              left: hover.x + 14,
              top: hover.y + 10,
              background: C.tielDark,
              color: "#fff",
              borderRadius: 8,
              padding: "8px 11px",
              fontSize: 12.5,
              maxWidth: 300,
              pointerEvents: "none",
              zIndex: 10,
              boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{hover.nodo.caption ?? "(sin nombre)"}</div>
            <div style={{ opacity: 0.85 }}>
              {schemaEs(hover.nodo.schema)}
              {hover.nodo.pais ? ` · ${hover.nodo.pais}` : ""}
              {` · a ${String(hover.nodo.dist)} salto${hover.nodo.dist === 1 ? "" : "s"}`}
            </div>
            {hover.nodo.sancionado ? (
              <div style={{ color: "#F4B8A9", fontWeight: 700, marginTop: 2 }}>Sanción vigente</div>
            ) : null}
            {hover.nodo.flag_severidad ? (
              <div style={{ color: "#F2DBA6", fontWeight: 700, marginTop: 2 }}>
                Situación fiscal SAT ({hover.nodo.flag_severidad})
              </div>
            ) : null}
            <div style={{ opacity: 0.7, marginTop: 3, fontSize: 11.5 }}>clic: abrir ficha · arrastrar: mover</div>
          </div>
        ) : null}

        <div
          style={{
            position: "absolute",
            left: 10,
            bottom: 10,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            padding: "8px 12px",
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            fontSize: 12,
            color: C.ink,
            alignItems: "center",
          }}
        >
          {schemasPresentes.map((s) => (
            <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: nodoColor(s), border: "1px solid rgba(0,0,0,0.15)" }} />
              {schemaEs(s)}
            </span>
          ))}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", border: `2px solid ${C.red}` }} />
            sanción vigente
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", border: `2px dashed ${C.amber}` }} />
            situación fiscal SAT
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", border: `2.5px solid ${C.oro}` }} />
            centro
          </span>
        </div>
      </div>

      <p style={{ fontSize: 11.5, color: C.muted, margin: "8px 2px" }}>
        Rueda: acercar · arrastrar fondo: mover el lienzo · arrastrar nodo: acomodar · clic en nodo: abrir su ficha.
        Las relaciones en tabla (accesible) viven en la ficha de cada entidad.
      </p>
    </div>
  );
}
